import { isReadOnlyViewer } from '../../utils/hackathonStatus';
import React, { useEffect, useMemo, useState } from 'react';
import { currentTeamOf, teamJoinBlockedReason, teamsLockedReason } from '../../utils/hackathonStatus';

import { useNavigate, useParams } from 'react-router-dom';
import { useIdea } from '../../hooks/useIdea';
import { Loading } from '../../components/Loading';
import { IdeaCard } from '../../components/Idea/IdeaCard';
import { useHackathon } from '../../hooks/useHackathon';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { NewComment } from '../../components/Comment/NewComment';
import { Comment } from '../../components/Comment/Comment';
import { JoinHackathon } from '../../components/Invite/JoinHackathon';
import { IdeaVoting } from '../../components/Voting/IdeaVoting';
import { TeamFeedback } from '../../components/Voting/TeamFeedback';
import { ParticipantVotingNotice } from '../../components/Voting/ParticipantVotingNotice';
import { useAuth } from '../../services/useAuth';
import { PageContainer } from '../../components/PageContainer';
import { LiveVotingBanner } from '../../components/Voting/LiveVotingBanner';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useHackathonSync } from '../../hooks/useHackathonSync';
import { HelpLink } from '../../components/HelpLink';
import { participantsScore } from '../../utils/sessions';
dayjs.extend(relativeTime);

export const FullIdeaPage = () => {
    const {
        id,
        ideaId
    } = useParams();

    const { idea, loading, error, reload } = useIdea(id, ideaId);

    const { hackathon, loading: loadingHackathon, error: errorHackathon, reload: reloadHackathon } = useHackathon(id);

    // a page left open follows the event: the phase at once from the live board, the idea and teams when the board says they changed
    const board = useLiveBoard(id);
    useHackathonSync(id, board.version, hackathon?.participants.length || 0, reload);
    // every hackathon has a room, so an empty board just hasn't loaded yet
    const boardLoaded = board.sessions.length > 0;
    const live = useMemo(() => hackathon && {
        ...hackathon,
        votingPeriodOn: boardLoaded ? board.votingPeriodOn : hackathon.votingPeriodOn,
        resultsAnnounced: boardLoaded ? !!board.resultsAnnounced : hackathon.winners.length > 0
    }, [hackathon, boardLoaded, board.votingPeriodOn, board.resultsAnnounced]);

    const [scrolled, setScrolled] = useState(false);

    const { user } = useAuth();

    const navigate = useNavigate();

    const isJudge = useMemo(() => {
        if (!user || !hackathon) {
            return false;
        }

        return hackathon.judges.some(o => o.email === user.email);
    }, [user, hackathon])

    const hash = window.location.hash;
    useEffect(() => {
        if (idea && hash === '#voting' && !scrolled) {
            document.getElementById('voting')?.scrollIntoView();
            setScrolled(true);
        }
    }, [hash, idea, scrolled])

    const joinBlockedReason = useMemo(() => {
        if (!hackathon) {
            return 'Loading';
        }
        return teamJoinBlockedReason(live || hackathon, user ? user.email : undefined);
    }, [user, hackathon, live])


    const votingPeriodOn = useMemo(() => {
        return live?.votingPeriodOn;
    }, [live])

    if (!user) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>Not logged in</h1>
            </Stack>
        </PageContainer>
    }

    // only the first load: reloads after joining or leaving keep the page on screen
    if ((loading && !idea) || (loadingHackathon && !hackathon)) {
        return <Loading />
    }

    if (!id || !ideaId) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>No hackathon or idea id provided</h1>
            </Stack>
        </PageContainer>
    }

    if (errorHackathon) {
        const data = (errorHackathon.response as any).data;
        const needsInviteCode = data.needsInviteCode || true;

        return <PageContainer>
            <Stack spacing={2}>
                <JoinHackathon hackathonId={id} needsInviteCode={needsInviteCode} />
            </Stack>
        </PageContainer>
    }

    if (error) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>Error loading idea</h1>
            </Stack>
        </PageContainer>
    }


    if (!idea || !hackathon) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>Idea not found</h1>
            </Stack>
        </PageContainer>
    }

    return (
        <PageContainer>
            <Stack spacing={2}>
                <LiveVotingBanner hackathonId={id} />
                <AppBreadcrumbs crumbs={[
                    {
                        type: PageType.Home,
                        link: '/',
                        name: 'Home'
                    },
                    {
                        type: PageType.Hackathon,
                        link: `/h/${id}`,
                        name: hackathon?.name || ''
                    },
                    {
                        type: PageType.Idea,
                        name: idea.title
                    }
                ]} />

                <IdeaCard
                    ideaId={idea._id}
                    hackathonId={id}
                    maxTeamSize={hackathon?.maxTeamSize}
                    isOrganizer={hackathon?.organizers.some(o => o.email === user.email)}
                    joinBlockedReason={joinBlockedReason}
                    currentTeam={hackathon ? currentTeamOf(hackathon, user.email) : undefined}
                    showJoin={!isJudge || !!hackathon?.organizers.some(o => o.email === user.email)}
                    editLocked={!!live?.resultsAnnounced}
                    teamsLocked={!!live && !!teamsLockedReason(live)}
                    readOnly={!!hackathon && isReadOnlyViewer(hackathon, user.email)}
                    onDelete={() => {
                        // redirect to hackathon page
                        navigate(`/h/${id}`);
                    }}
                    onReloadIdea={() => {
                        // reload the idea, and the hackathon too: who's on which team decides whether Join is allowed
                        reload();
                        reloadHackathon && reloadHackathon();
                    }}
                />
                {
                    // participants look for voting here too; say so when only the judges score
                    <ParticipantVotingNotice hackathonId={id} hackathon={hackathon} where="idea"
                        hidden={isJudge || hackathon.organizers.some(o => o.email === user.email)} />
                }
                {
                    (participantsScore(hackathon) || isJudge) && votingPeriodOn && (
                        <Stack direction="column" spacing={1} id="voting" sx={{ scrollMarginTop: 88, pt: 2 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                <h3>Your vote</h3>
                                <HelpLink guide={isJudge ? 'vote' : 'score'}>How scoring works</HelpLink>
                            </Stack>
                            <IdeaVoting hackathonId={id} hackathon={hackathon} idea={idea} />
                        </Stack>
                    )
                }
                {
                    // the team's own judge feedback, once the results are out
                    !!live?.resultsAnnounced && (idea.members.some(m => m.email === user.email) || hackathon.organizers.some(o => o.email === user.email)) && (
                        <TeamFeedback hackathonId={id} ideaId={idea._id} />
                    )
                }
                <Stack spacing={2} direction="column" sx={{
                    backgroundColor: 'var(--surface-muted)',
                    borderRadius: '14px'
                }} padding={{ xs: 2, md: 3 }}>
                    <h3>Comments ({idea.comments.length})</h3>
                    {
                        idea.comments.map(comment => {
                            return <Comment key={comment._id} {...comment} />
                        })
                    }
                    {
                        idea.comments.length === 0 && <Typography>No comments yet</Typography>
                    }
                    {!(hackathon && isReadOnlyViewer(hackathon, user.email)) && <Stack spacing={2} direction="column">
                        <NewComment hackathonId={id} ideaId={ideaId} submitted={() => {
                            reload();
                        }} />
                    </Stack>}
                </Stack>
            </Stack>
        </PageContainer>
    )
}